import React, { JSX, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { routeChangeEvent, SessionStoragePersistence } from '@ibe/services';
import { useExternalSearchParams } from './Config/ExternalParamsContext';

class LocationProps {
  pathname = '';

  search = '';
}

const persist = new SessionStoragePersistence<LocationProps>(LocationProps);

const RouteBroadcast = ({ mode = 'DEFAULT' }: { mode?: 'DEFAULT' | 'WIDGET' }): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const context = useExternalSearchParams();
  const { pathname, search } = location;
  const isMounted = useRef<boolean>(false);

  useEffect(() => {
    const urlObject = new URL(`${location.pathname}${location.search}`, window.location.origin);
    if (!urlObject.searchParams.get('fromOnPopstate')) {
      window.history.pushState({ url: `${urlObject.pathname}${urlObject.search}` }, '');
    }
  }, [location]);

  useEffect(() => {
    const callback = (evt: PopStateEvent) => {
      const newUrl = evt?.state?.url;
      if (newUrl) {
        const urlObject = new URL(newUrl, window.location.origin);
        urlObject.searchParams.append('fromOnPopstate', 'true');
        navigate(`${urlObject.pathname}${urlObject.search}`);
      }
    };

    window.addEventListener('popstate', callback);

    return () => {
      window.removeEventListener('popstate', callback);
    };
  }, []);

  useEffect(() => {
    routeChangeEvent.broadcast(location);

    if (mode === 'WIDGET') {
      persist.useKey(`${context?.widgetSessionKeyPrefix || ''}iso__memory-router__location`);
      if (isMounted.current) {
        persist.set({ pathname, search });
      } else {
        isMounted.current = true;
        if (persist.exists()) {
          const { pathname: persistedPathname, search: persistedSearch } = persist.get();
          navigate(`${persistedPathname}${persistedSearch}`);
        }
      }
    }

    if ('parentIFrame' in window) {
      window.parentIFrame.scrollTo(0, 0);
      // It's the way how to use scrollTo from iframe-resizer used for meg project.
      // https://github.com/davidjbradshaw/iframe-resizer/blob/master/docs/iframed_page/methods.md
    }
  }, [pathname, search]);

  return <></>;
};

export default RouteBroadcast;
